import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  FileText, Send, Loader2, Calendar, Users, Briefcase,
  Clock, Mail, Trash2, Eye, Pencil, Save, X,
  Sparkles, Activity, AlertTriangle, Target, Check,
  Plus, Minus, Package, Code, Bug, Lightbulb, Megaphone, TrendingUp,
  Settings as SettingsIcon,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CustomSelect } from '@/components/ui/custom-select';
import { ReportTemplatesPanel } from '@/pages/ReportTemplates';

// Top-level tab keys must stay URL-friendly because they show up in ?tab=.
const REPORTS_TAB_KEYS = ['reports', 'standups', 'templates'];
const REPORTS_DEFAULT_TAB = 'reports';
const REPORTS_TAB_TRIGGER_CLASS =
  'data-[state=active]:bg-[#98139C] data-[state=active]:text-white ' +
  'data-[state=active]:shadow-sm dark:data-[state=active]:bg-[#98139C] ' +
  'text-[#30302F] dark:text-white/80 ' +
  'motion-reduce:transition-none';

const reportTypeConfig = {
  weekly: { label: 'Weekly Progress', icon: Calendar, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400' },
  sprint_wrap: { label: 'Sprint Wrap (team email)', icon: Target, color: 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/20 dark:text-fuchsia-300' },
  sprint_summary: { label: 'Sprint Summary', icon: Activity, color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400' },
  stakeholder_update: { label: 'Stakeholder Update', icon: Users, color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/20 dark:text-cyan-400' },
  team_sprint_report: { label: 'Team Sprint Report', icon: Target, color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400' },
  executive: { label: 'Executive Update', icon: Briefcase, color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400' },
  release_notes: { label: 'Release Notes', icon: FileText, color: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400' }
};

const audienceConfig = {
  executive: { label: 'Executive', icon: Briefcase },
  technical: { label: 'Technical', icon: Activity },
  customer: { label: 'Customer', icon: Users },
  stakeholder: { label: 'Stakeholder', icon: Target }
};

// =============================================
// EDITABLE FIELD HELPERS
// =============================================

function EditableText({ value, onChange, editing, multiline = false, className = '' }) {
  if (!editing) {
    return <span className={className}>{value || '—'}</span>;
  }
  if (multiline) {
    return (
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className={`w-full border border-[#98139C]/30 rounded px-2 py-1.5 text-sm bg-white dark:bg-white/10 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#98139C] resize-y ${className}`}
      />
    );
  }
  return (
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full border border-[#98139C]/30 rounded px-2 py-1 text-sm bg-white dark:bg-white/10 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#98139C] ${className}`}
    />
  );
}

function EditableList({ items, onChange, editing, renderItem }) {
  if (!editing) {
    return (
      <ul className="space-y-1">
        {(items || []).map((item, i) => (
          <li key={i}>{renderItem ? renderItem(item, i) : (
            <span className="text-sm text-[#30302F] dark:text-white flex items-start gap-2">
              <span className="text-green-500 mt-0.5">&#10003;</span> {item}
            </span>
          )}</li>
        ))}
      </ul>
    );
  }
  return (
    <div className="space-y-1.5">
      {(items || []).map((item, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            type="text"
            value={typeof item === 'string' ? item : JSON.stringify(item)}
            onChange={(e) => {
              const updated = [...items];
              updated[i] = e.target.value;
              onChange(updated);
            }}
            className="flex-1 border border-[#98139C]/30 rounded px-2 py-1 text-sm bg-white dark:bg-white/10 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#98139C]"
          />
          <button
            onClick={() => onChange(items.filter((_, idx) => idx !== i))}
            className="p-1 text-red-400 hover:text-red-600"
          >
            <Minus size={14} />
          </button>
        </div>
      ))}
      <button
        onClick={() => onChange([...(items || []), ''])}
        className="flex items-center gap-1 text-xs text-[#98139C] hover:text-[#7a0f7d] font-medium"
      >
        <Plus size={12} /> Add item
      </button>
    </div>
  );
}

function SectionHeader({ icon: Icon, label, color = 'text-[#4F0077] dark:text-[#d87bdb]' }) {
  return (
    <h4 className={`text-[11px] uppercase tracking-[0.9px] font-bold ${color} mb-2 flex items-center gap-1.5`}>
      {Icon && <Icon size={13} />} {label}
    </h4>
  );
}

function MetricCard({ value, label, color = 'text-[#30302F] dark:text-white' }) {
  return (
    <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-3 text-center">
      <p className={`text-2xl font-bold ${color}`}>{value ?? 'N/A'}</p>
      <p className="text-[11px] text-[#30302F]/80 dark:text-white/80 uppercase">{label}</p>
    </div>
  );
}

// =============================================
// TYPE-SPECIFIC REPORT RENDERERS
// =============================================

function renderWeeklyReport(c, editing, setField) {
  return (
    <>
      {/* Date header */}
      {c.week_of && (
        <p className="text-sm text-[#30302F]/80 dark:text-white/80">
          <Calendar size={13} className="inline mr-1" /> Week of {c.week_of}
        </p>
      )}

      {/* Executive Summary */}
      <div className="bg-[#faf8fb] dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
        <SectionHeader icon={Sparkles} label="Executive Summary" color="text-[#98139C]" />
        <EditableText value={c.executive_summary} onChange={(v) => setField('executive_summary', v)} editing={editing} multiline className="text-sm text-[#30302F] dark:text-white leading-relaxed" />
      </div>

      {/* Metrics */}
      {c.metrics && (
        <div className="grid grid-cols-2 gap-3">
          <MetricCard value={c.metrics.stories_completed} label="Stories Completed" color="text-green-600" />
          <MetricCard value={c.metrics.points_delivered} label="Points Delivered" color="text-[#98139C]" />
        </div>
      )}

      {/* Key Achievements */}
      {(c.key_achievements?.length > 0 || editing) && (
        <div>
          <SectionHeader icon={Check} label="Key Achievements" color="text-green-600 dark:text-green-400" />
          <EditableList items={c.key_achievements} onChange={(v) => setField('key_achievements', v)} editing={editing}
            renderItem={(item) => (
              <span className="text-sm text-[#30302F] dark:text-white flex items-start gap-2">
                <span className="text-green-500 mt-0.5">&#10003;</span> {item}
              </span>
            )}
          />
        </div>
      )}

      {/* Highlights */}
      {c.highlights?.length > 0 && (
        <div>
          <SectionHeader icon={Sparkles} label="Highlights" color="text-blue-600 dark:text-blue-400" />
          <div className="space-y-2">
            {c.highlights.map((h, i) => (
              <div key={i} className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-3">
                <p className="font-medium text-sm text-[#30302F] dark:text-white">{typeof h === 'object' ? h.title : h}</p>
                {h.impact && <p className="text-xs text-[#30302F]/80 dark:text-white/80 mt-0.5">{h.impact}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming */}
      {(c.upcoming?.length > 0 || editing) && (
        <div>
          <SectionHeader icon={Target} label="Upcoming" />
          <EditableList items={c.upcoming} onChange={(v) => setField('upcoming', v)} editing={editing}
            renderItem={(item) => (
              <span className="text-sm text-[#30302F] dark:text-white flex items-start gap-2">
                <span className="text-[#4F0077] dark:text-[#d87bdb] mt-0.5">&#9670;</span> {item}
              </span>
            )}
          />
        </div>
      )}

      {/* Decisions Needed */}
      {(c.decisions_needed?.length > 0 || editing) && (
        <div className="bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-700/30 p-4">
          <SectionHeader icon={AlertTriangle} label="Decisions Needed" color="text-purple-600 dark:text-purple-400" />
          <EditableList items={c.decisions_needed} onChange={(v) => setField('decisions_needed', v)} editing={editing}
            renderItem={(item) => (
              <span className="text-sm text-purple-700 dark:text-purple-300 flex items-start gap-2">
                <span className="text-purple-500 mt-0.5">&#9670;</span> {item}
              </span>
            )}
          />
        </div>
      )}

      {/* Customer Facing Notes */}
      {c.customer_facing_notes?.length > 0 && (
        <div>
          <SectionHeader icon={Megaphone} label="Customer-Facing Notes" color="text-cyan-600 dark:text-cyan-400" />
          <ul className="space-y-1">
            {c.customer_facing_notes.map((note, i) => (
              <li key={i} className="text-sm text-[#30302F] dark:text-white flex items-start gap-2">
                <span className="text-cyan-500 mt-0.5">&#8226;</span> {note}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

function renderSprintWrap(c, editing, setField) {
  const r = c.results || {};
  const shipped = Array.isArray(c.what_shipped) ? c.what_shipped : [];
  const blockers = Array.isArray(c.blocker_updates) ? c.blocker_updates : [];
  const carry = Array.isArray(c.carryover_to_next_sprint) ? c.carryover_to_next_sprint : [];

  const setResultsField = (k, v) => setField('results', { ...r, [k]: v });
  return (
    <>
      {/* Subject */}
      {(c.subject || editing) && (
        <div>
          <SectionHeader icon={Megaphone} label="Subject" />
          <EditableText value={c.subject} onChange={(v) => setField('subject', v)} editing={editing} className="text-sm font-medium text-[#30302F] dark:text-white" />
        </div>
      )}

      {/* Headline */}
      <div className="bg-[#faf8fb] dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 rounded-lg p-4">
        <SectionHeader icon={Sparkles} label="Headline" color="text-[#98139C]" />
        <EditableText value={c.headline} onChange={(v) => setField('headline', v)} editing={editing} multiline className="text-sm text-[#30302F] dark:text-white leading-relaxed font-medium" />
      </div>

      {/* Sprint goal recap */}
      {(c.sprint_goal_recap || editing) && (
        <div>
          <SectionHeader icon={Target} label="Sprint goal (recap)" />
          <EditableText value={c.sprint_goal_recap} onChange={(v) => setField('sprint_goal_recap', v)} editing={editing} multiline className="text-sm text-[#30302F] dark:text-white leading-relaxed" />
        </div>
      )}

      {/* Results table */}
      <div>
        <SectionHeader icon={Activity} label="Results" />
        <div className="overflow-x-auto border border-[#e3e3e3] dark:border-white/10 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-[#faf8fb] dark:bg-white/5">
              <tr>
                <th className="text-left p-3 font-bold text-[11px] uppercase tracking-[0.9px] text-[#30302F] dark:text-white"> </th>
                <th className="text-right p-3 font-bold text-[11px] uppercase tracking-[0.9px] text-[#30302F] dark:text-white">User Stories</th>
                <th className="text-right p-3 font-bold text-[11px] uppercase tracking-[0.9px] text-[#30302F] dark:text-white">Story Points</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Planned', 'planned_stories', 'planned_points'],
                ['Delivered', 'delivered_stories', 'delivered_points'],
                ['Carryover to next sprint', 'carryover_stories', 'carryover_points'],
              ].map(([label, sKey, pKey]) => (
                <tr key={label} className="border-t border-[#e3e3e3] dark:border-white/10">
                  <td className="p-3 font-medium text-[#30302F] dark:text-white">{label}</td>
                  <td className="p-3 text-right text-[#30302F] dark:text-white">
                    <EditableText value={r[sKey] ?? 0} onChange={(v) => setResultsField(sKey, Number(v) || 0)} editing={editing} className="text-right inline-block w-16" />
                  </td>
                  <td className="p-3 text-right text-[#30302F] dark:text-white">
                    <EditableText value={r[pKey] ?? 0} onChange={(v) => setResultsField(pKey, Number(v) || 0)} editing={editing} className="text-right inline-block w-16" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {(r.context_note || editing) && (
          <div className="mt-2">
            <EditableText value={r.context_note} onChange={(v) => setResultsField('context_note', v)} editing={editing} multiline className="text-xs text-[#30302F]/70 dark:text-white/70 italic" />
          </div>
        )}
      </div>

      {/* What shipped, grouped by capability */}
      {(shipped.length > 0 || editing) && (
        <div>
          <SectionHeader icon={Check} label="What shipped" color="text-green-600 dark:text-green-400" />
          <div className="space-y-3">
            {shipped.map((g, i) => (
              <div key={i} className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 rounded-lg p-3">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#4F0077] dark:text-[#d87bdb]">{g.capability || '—'}</span>
                  <EditableText value={g.headline} onChange={(v) => setField('what_shipped', shipped.map((x, j) => j === i ? { ...x, headline: v } : x))} editing={editing} className="text-sm font-medium text-[#30302F] dark:text-white flex-1" />
                </div>
                {Array.isArray(g.stories) && g.stories.length > 0 && (
                  <ul className="text-xs text-[#30302F]/80 dark:text-white/80 mt-1 space-y-0.5 pl-3">
                    {g.stories.map((s, j) => (
                      <li key={j}>
                        {s.title}
                        {s.ado_id ? <span className="text-[#30302F]/50 dark:text-white/50"> #{s.ado_id}</span> : null}
                        {s.assignee && s.assignee !== 'Unassigned' ? <span className="text-[#30302F]/60 dark:text-white/60"> — {s.assignee}</span> : null}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Blocker updates */}
      {(blockers.length > 0 || editing) && (
        <div>
          <SectionHeader icon={AlertTriangle} label="Blocker update" color="text-amber-600 dark:text-amber-400" />
          <div className="space-y-2">
            {blockers.map((b, i) => (
              <div key={i} className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700/30 rounded-lg p-3">
                <div className="text-sm font-medium text-[#30302F] dark:text-white">
                  {b.title}
                  {b.ado_id ? <span className="text-[#30302F]/50 dark:text-white/50 text-xs"> #{b.ado_id}</span> : null}
                  {b.owner ? <span className="text-[#30302F]/60 dark:text-white/60 text-xs"> ({b.owner})</span> : null}
                </div>
                <EditableText value={b.summary} onChange={(v) => setField('blocker_updates', blockers.map((x, j) => j === i ? { ...x, summary: v } : x))} editing={editing} multiline className="text-xs text-amber-900 dark:text-amber-200 mt-1" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Carryover */}
      {(carry.length > 0 || editing) && (
        <div>
          <SectionHeader icon={Calendar} label="Carryover to next sprint" />
          <ul className="text-sm text-[#30302F] dark:text-white space-y-1">
            {carry.map((c2, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-[#4F0077] dark:text-[#d87bdb] mt-0.5">&#9670;</span>
                <span>
                  {c2.title}
                  {c2.ado_id ? <span className="text-[#30302F]/50 dark:text-white/50 text-xs"> #{c2.ado_id}</span> : null}
                  {c2.points ? <span className="text-[#30302F]/60 dark:text-white/60 text-xs"> ({c2.points} SP)</span> : null}
                  {c2.assignee && c2.assignee !== 'Unassigned' ? <span className="text-[#30302F]/60 dark:text-white/60"> — {c2.assignee}</span> : null}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Outlook */}
      {(c.next_sprint_outlook || editing) && (
        <div>
          <SectionHeader icon={Target} label="Outlook" />
          <EditableText value={c.next_sprint_outlook} onChange={(v) => setField('next_sprint_outlook', v)} editing={editing} multiline className="text-sm text-[#30302F] dark:text-white leading-relaxed" />
        </div>
      )}

      {/* Closing line */}
      {(c.closing_line || editing) && (
        <div className="bg-[#faf8fb] dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 rounded-lg p-3">
          <EditableText value={c.closing_line} onChange={(v) => setField('closing_line', v)} editing={editing} multiline className="text-sm italic text-[#30302F] dark:text-white" />
        </div>
      )}
    </>
  );
}


function renderSprintSummary(c, editing, setField) {
  return (
    <>
      {/* Sprint Header */}
      {c.sprint_name && (
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-bold text-[#30302F] dark:text-white">{c.sprint_name}</span>
          {c.goal_achieved !== undefined && (
            <Badge className={c.goal_achieved ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'}>
              {c.goal_achieved ? '&#10003; Goal Achieved' : '&#10007; Goal Not Met'}
            </Badge>
          )}
        </div>
      )}

      {/* Summary */}
      <div className="bg-[#faf8fb] dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
        <SectionHeader icon={Activity} label="Sprint Summary" color="text-[#98139C]" />
        <EditableText value={c.summary} onChange={(v) => setField('summary', v)} editing={editing} multiline className="text-sm text-[#30302F] dark:text-white leading-relaxed" />
      </div>

      {/* Goal Notes */}
      {(c.goal_notes || editing) && (
        <div>
          <SectionHeader icon={Target} label="Goal Notes" color="text-[#98139C]" />
          <EditableText value={c.goal_notes} onChange={(v) => setField('goal_notes', v)} editing={editing} multiline className="text-sm text-[#30302F] dark:text-white" />
        </div>
      )}

      {/* Metrics */}
      <div className="grid grid-cols-2 gap-3">
        <MetricCard value={c.stories_completed} label="Stories Completed" color="text-green-600" />
        <MetricCard value={c.points_delivered} label="Points Delivered" color="text-[#98139C]" />
      </div>

      {/* Highlights */}
      {(c.highlights?.length > 0 || editing) && (
        <div>
          <SectionHeader icon={Sparkles} label="Highlights" color="text-green-600 dark:text-green-400" />
          <EditableList items={c.highlights} onChange={(v) => setField('highlights', v)} editing={editing}
            renderItem={(item) => (
              <span className="text-sm text-[#30302F] dark:text-white flex items-start gap-2">
                <span className="text-green-500 mt-0.5">&#10003;</span> {item}
              </span>
            )}
          />
        </div>
      )}

      {/* Challenges */}
      {(c.challenges?.length > 0 || editing) && (
        <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700/30 p-4">
          <SectionHeader icon={AlertTriangle} label="Challenges" color="text-amber-600 dark:text-amber-400" />
          <EditableList items={c.challenges} onChange={(v) => setField('challenges', v)} editing={editing}
            renderItem={(item) => (
              <span className="text-sm text-amber-700 dark:text-amber-300 flex items-start gap-2">
                <span className="text-amber-500 mt-0.5">&#9888;</span> {item}
              </span>
            )}
          />
        </div>
      )}

      {/* Learnings */}
      {(c.learnings?.length > 0 || editing) && (
        <div>
          <SectionHeader icon={Lightbulb} label="Learnings" color="text-blue-600 dark:text-blue-400" />
          <EditableList items={c.learnings} onChange={(v) => setField('learnings', v)} editing={editing}
            renderItem={(item) => (
              <span className="text-sm text-[#30302F] dark:text-white flex items-start gap-2">
                <span className="text-blue-500 mt-0.5">&#128161;</span> {item}
              </span>
            )}
          />
        </div>
      )}

      {/* Next Sprint Preview */}
      {(c.next_sprint_preview || editing) && (
        <div className="bg-[#faf8fb] dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
          <SectionHeader icon={Target} label="Next Sprint Preview" color="text-[#98139C]" />
          <EditableText value={c.next_sprint_preview} onChange={(v) => setField('next_sprint_preview', v)} editing={editing} multiline className="text-sm text-[#30302F] dark:text-white" />
        </div>
      )}
    </>
  );
}

function renderStakeholderUpdate(c, editing, setField) {
  return (
    <>
      {/* Executive Summary */}
      <div className="bg-[#faf8fb] dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
        <SectionHeader icon={Sparkles} label="Executive Summary" color="text-[#98139C]" />
        <EditableText value={c.executive_summary} onChange={(v) => setField('executive_summary', v)} editing={editing} multiline className="text-sm text-[#30302F] dark:text-white leading-relaxed" />
      </div>

      {/* Progress Highlights */}
      {c.progress_highlights?.length > 0 && (
        <div>
          <SectionHeader icon={Activity} label="Progress Highlights" />
          <div className="space-y-2">
            {c.progress_highlights.map((item, i) => (
              <div key={i} className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-3 flex items-start justify-between">
                <div>
                  <p className="font-medium text-sm text-[#30302F] dark:text-white">{item.area}</p>
                  <p className="text-xs text-[#30302F]/80 dark:text-white/80 mt-0.5">{item.summary}</p>
                </div>
                <Badge className={`text-[10px] ${
                  item.status === 'On Track' ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400' :
                  item.status === 'At Risk' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400' :
                  'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                }`}>{item.status}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Key Decisions Made */}
      {(c.key_decisions_made?.length > 0 || editing) && (
        <div>
          <SectionHeader icon={Check} label="Key Decisions Made" color="text-blue-600 dark:text-blue-400" />
          <EditableList items={c.key_decisions_made} onChange={(v) => setField('key_decisions_made', v)} editing={editing}
            renderItem={(item) => (
              <span className="text-sm text-[#30302F] dark:text-white flex items-start gap-2">
                <span className="text-blue-500 mt-0.5">&#10003;</span> {item}
              </span>
            )}
          />
        </div>
      )}

      {/* Upcoming Milestones */}
      {c.upcoming_milestones?.length > 0 && (
        <div>
          <SectionHeader icon={Target} label="Upcoming Milestones" color="text-[#98139C]" />
          <div className="space-y-2">
            {c.upcoming_milestones.map((item, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b border-[#e3e3e3] dark:border-white/10 pb-2">
                <span className="text-[#30302F] dark:text-white">{item.milestone}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#30302F]/80 dark:text-white/80">{item.target_date}</span>
                  <Badge className={`text-[10px] ${
                    item.status === 'On Track' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                  }`}>{item.status}</Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Metrics Snapshot */}
      {c.metrics_snapshot && (
        <div className="grid grid-cols-3 gap-3">
          <MetricCard value={c.metrics_snapshot.sprint_progress} label="Sprint Progress" color="text-[#98139C]" />
          <MetricCard value={c.metrics_snapshot.velocity} label="Velocity" color="text-[#4F0077]" />
          <MetricCard value={c.metrics_snapshot.items_in_backlog} label="Backlog Items" />
        </div>
      )}

      {/* Decisions Needed */}
      {(c.decisions_needed?.length > 0 || editing) && (
        <div className="bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-700/30 p-4">
          <SectionHeader icon={AlertTriangle} label="Decisions Needed" color="text-purple-600 dark:text-purple-400" />
          <EditableList items={c.decisions_needed} onChange={(v) => setField('decisions_needed', v)} editing={editing}
            renderItem={(item) => (
              <span className="text-sm text-purple-700 dark:text-purple-300 flex items-start gap-2">
                <span className="text-purple-500 mt-0.5">&#9670;</span> {item}
              </span>
            )}
          />
        </div>
      )}
    </>
  );
}

function renderTeamSprintReport(c, editing, setField) {
  return (
    <>
      {/* Sprint Info Header */}
      {c.sprint_name && (
        <div className="flex items-center gap-4 text-sm text-[#30302F]/80 dark:text-white/80">
          <span><strong className="text-[#30302F] dark:text-white">{c.sprint_name}</strong></span>
          {c.sprint_dates && (
            <span>{c.sprint_dates.start} &rarr; {c.sprint_dates.end}</span>
          )}
        </div>
      )}

      {/* Sprint Summary */}
      <div className="bg-[#faf8fb] dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
        <SectionHeader icon={Activity} label="Sprint Summary" color="text-[#98139C]" />
        <EditableText value={c.sprint_summary || c.summary} onChange={(v) => setField('sprint_summary', v)} editing={editing} multiline className="text-sm text-[#30302F] dark:text-white leading-relaxed" />
      </div>

      {/* Velocity Stats */}
      {c.velocity && (
        <div className="grid grid-cols-3 gap-3">
          <MetricCard value={c.velocity.planned || 0} label="Planned Points" />
          <MetricCard value={c.velocity.completed || 0} label="Completed" color="text-green-600" />
          <MetricCard value={c.velocity.percentage || '0%'} label="Achievement" color="text-[#98139C]" />
        </div>
      )}

      {/* Stories Breakdown */}
      {c.stories_breakdown && (
        <div className="grid grid-cols-5 gap-2 text-center">
          {Object.entries(c.stories_breakdown).map(([key, value]) => (
            <div key={key} className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-2">
              <p className="text-lg font-bold text-[#30302F] dark:text-white">{value}</p>
              <p className="text-[10px] text-[#30302F]/80 dark:text-white/80 uppercase">{key.replace(/_/g, ' ')}</p>
            </div>
          ))}
        </div>
      )}

      {/* Completed Work */}
      {c.completed_work?.length > 0 && (
        <div>
          <SectionHeader icon={Check} label="Completed Work" color="text-green-600 dark:text-green-400" />
          <div className="space-y-2">
            {c.completed_work.map((item, i) => (
              <div key={i} className="flex items-start justify-between border-b border-[#e3e3e3] dark:border-white/10 pb-2">
                <div>
                  <p className="text-sm text-[#30302F] dark:text-white">{item.title}</p>
                  {item.impact && <p className="text-xs text-[#30302F]/80 dark:text-white/80 mt-0.5">{item.impact}</p>}
                </div>
                {item.points && <Badge className="text-[10px] bg-green-100 text-green-700">{item.points} pts</Badge>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Blockers */}
      {c.blockers?.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/30 p-4">
          <SectionHeader icon={AlertTriangle} label="Blockers" color="text-red-600 dark:text-red-400" />
          <div className="space-y-2">
            {c.blockers.map((item, i) => (
              <div key={i} className="text-sm">
                <p className="text-red-700 dark:text-red-300 font-medium">{item.story}</p>
                <p className="text-red-600 dark:text-red-400 text-xs">{item.blocker}</p>
                {item.action && <p className="text-red-600/80 dark:text-red-400/80 text-xs mt-1">&rarr; {item.action}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Team Performance */}
      {c.team_performance?.length > 0 && (
        <div>
          <SectionHeader icon={Users} label="Team Performance" />
          <div className="grid grid-cols-2 gap-2">
            {c.team_performance.map((member, i) => (
              <div key={i} className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-3 flex items-center justify-between">
                <span className="text-sm font-medium text-[#30302F] dark:text-white">{member.member}</span>
                <div className="text-xs text-right">
                  <span className="text-green-600">{member.stories_completed} stories</span>
                  <span className="text-[#30302F]/60 dark:text-white/60 mx-1">|</span>
                  <span className="text-[#98139C]">{member.points_delivered} pts</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Retrospective Notes */}
      {c.retrospective_notes && (
        <div className="grid grid-cols-2 gap-4">
          {c.retrospective_notes.what_went_well?.length > 0 && (
            <div className="bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800/30 p-3">
              <h5 className="text-[10px] uppercase tracking-[0.9px] font-bold text-green-600 dark:text-green-400 mb-2">What Went Well</h5>
              <ul className="text-xs text-green-700 dark:text-green-300 space-y-1">
                {c.retrospective_notes.what_went_well.map((item, i) => (
                  <li key={i}>&#10003; {item}</li>
                ))}
              </ul>
            </div>
          )}
          {c.retrospective_notes.what_needs_improvement?.length > 0 && (
            <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 p-3">
              <h5 className="text-[10px] uppercase tracking-[0.9px] font-bold text-amber-600 dark:text-amber-400 mb-2">Needs Improvement</h5>
              <ul className="text-xs text-amber-700 dark:text-amber-300 space-y-1">
                {c.retrospective_notes.what_needs_improvement.map((item, i) => (
                  <li key={i}>&#9888; {item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Next Sprint Focus */}
      {(c.next_sprint_focus?.length > 0 || editing) && (
        <div>
          <SectionHeader icon={Target} label="Next Sprint Focus" color="text-[#98139C]" />
          <EditableList items={c.next_sprint_focus} onChange={(v) => setField('next_sprint_focus', v)} editing={editing}
            renderItem={(item) => (
              <span className="text-sm text-[#30302F] dark:text-white flex items-start gap-2">
                <span className="text-[#98139C] mt-0.5">&#9670;</span> {item}
              </span>
            )}
          />
        </div>
      )}
    </>
  );
}

function renderExecutiveReport(c, editing, setField) {
  return (
    <>
      {/* Date & Headline */}
      {c.date && (
        <p className="text-sm text-[#30302F]/80 dark:text-white/80">
          <Calendar size={13} className="inline mr-1" /> {c.date}
        </p>
      )}
      {(c.headline || editing) && (
        <div className="bg-gradient-to-r from-[#98139C]/5 to-[#4F0077]/5 dark:from-[#98139C]/10 dark:to-[#4F0077]/10 border border-[#98139C]/20 p-5">
          <EditableText value={c.headline} onChange={(v) => setField('headline', v)} editing={editing} className="text-xl font-heading font-semibold text-[#30302F] dark:text-white" />
        </div>
      )}

      {/* Key Metrics */}
      {c.key_metrics && (
        <div className={`grid grid-cols-${Math.min(Object.keys(c.key_metrics).length, 4)} gap-3`}>
          {Object.entries(c.key_metrics).map(([key, value]) => (
            <MetricCard key={key} value={value} label={key.replace(/_/g, ' ')} color="text-[#98139C]" />
          ))}
        </div>
      )}

      {/* Strategic Progress */}
      {(c.strategic_progress || editing) && (
        <div className="bg-[#faf8fb] dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
          <SectionHeader icon={TrendingUp} label="Strategic Progress" color="text-[#98139C]" />
          <EditableText value={c.strategic_progress} onChange={(v) => setField('strategic_progress', v)} editing={editing} multiline className="text-sm text-[#30302F] dark:text-white leading-relaxed" />
        </div>
      )}

      {/* Wins */}
      {(c.wins?.length > 0 || editing) && (
        <div>
          <SectionHeader icon={Check} label="Wins" color="text-green-600 dark:text-green-400" />
          <EditableList items={c.wins} onChange={(v) => setField('wins', v)} editing={editing}
            renderItem={(item) => (
              <span className="text-sm text-[#30302F] dark:text-white flex items-start gap-2">
                <span className="text-green-500 mt-0.5">&#10003;</span> {item}
              </span>
            )}
          />
        </div>
      )}

      {/* Risks */}
      {c.risks?.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700/30 p-4">
          <SectionHeader icon={AlertTriangle} label="Risks" color="text-amber-600 dark:text-amber-400" />
          <div className="space-y-2">
            {c.risks.map((item, i) => (
              <div key={i} className="text-sm">
                {typeof item === 'object' ? (
                  <>
                    <p className="font-medium text-amber-700 dark:text-amber-300">{item.risk}</p>
                    {item.mitigation && <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">&rarr; {item.mitigation}</p>}
                  </>
                ) : (
                  <p className="text-amber-700 dark:text-amber-300 flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5">&#9888;</span> {item}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ask */}
      {(c.ask || editing) && (
        <div className="bg-purple-50 dark:bg-purple-900/10 border-l-4 border-[#98139C] p-4">
          <SectionHeader icon={Megaphone} label="Ask / Decision Needed" color="text-[#98139C]" />
          <EditableText value={c.ask} onChange={(v) => setField('ask', v)} editing={editing} multiline className="text-sm text-[#30302F] dark:text-white" />
        </div>
      )}

      {/* Outlook */}
      {(c.outlook || editing) && (
        <div>
          <SectionHeader icon={Target} label="Outlook" color="text-[#4F0077] dark:text-[#d87bdb]" />
          <EditableText value={c.outlook} onChange={(v) => setField('outlook', v)} editing={editing} multiline className="text-sm text-[#30302F] dark:text-white" />
        </div>
      )}
    </>
  );
}

function renderReleaseNotes(c, editing, setField) {
  return (
    <>
      {/* Version Header */}
      <div className="flex items-center gap-3 flex-wrap">
        {c.version && <Badge className="bg-[#98139C] text-white text-sm px-3 py-1">v{c.version}</Badge>}
        {c.release_date && (
          <span className="text-sm text-[#30302F]/80 dark:text-white/80">
            <Calendar size={13} className="inline mr-1" /> {c.release_date}
          </span>
        )}
      </div>

      {/* Summary */}
      {(c.summary || editing) && (
        <div className="bg-[#faf8fb] dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
          <SectionHeader icon={FileText} label="Summary" color="text-[#98139C]" />
          <EditableText value={c.summary} onChange={(v) => setField('summary', v)} editing={editing} multiline className="text-sm text-[#30302F] dark:text-white leading-relaxed" />
        </div>
      )}

      {/* New Features */}
      {c.new_features?.length > 0 && (
        <div>
          <SectionHeader icon={Sparkles} label="New Features" color="text-green-600 dark:text-green-400" />
          <div className="space-y-2">
            {c.new_features.map((f, i) => (
              <div key={i} className="bg-white dark:bg-white/5 border border-green-200 dark:border-green-800/30 p-3 border-l-4 border-l-green-500">
                <p className="font-medium text-sm text-[#30302F] dark:text-white">{f.feature}</p>
                <p className="text-xs text-[#30302F]/80 dark:text-white/80 mt-0.5">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Improvements */}
      {c.improvements?.length > 0 && (
        <div>
          <SectionHeader icon={TrendingUp} label="Improvements" color="text-blue-600 dark:text-blue-400" />
          <div className="space-y-2">
            {c.improvements.map((imp, i) => (
              <div key={i} className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-3 border-l-4 border-l-blue-500">
                <p className="font-medium text-sm text-[#30302F] dark:text-white">{imp.area}</p>
                <p className="text-xs text-[#30302F]/80 dark:text-white/80 mt-0.5">{imp.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bug Fixes */}
      {c.bug_fixes?.length > 0 && (
        <div>
          <SectionHeader icon={Bug} label="Bug Fixes" color="text-amber-600 dark:text-amber-400" />
          <div className="space-y-2">
            {c.bug_fixes.map((fix, i) => (
              <div key={i} className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-3 border-l-4 border-l-amber-500">
                <p className="font-medium text-sm text-[#30302F] dark:text-white">{fix.issue}</p>
                <p className="text-xs text-[#30302F]/80 dark:text-white/80 mt-0.5">{fix.resolution}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Known Issues */}
      {(c.known_issues?.length > 0 || editing) && (
        <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700/30 p-4">
          <SectionHeader icon={AlertTriangle} label="Known Issues" color="text-amber-600 dark:text-amber-400" />
          <EditableList items={c.known_issues} onChange={(v) => setField('known_issues', v)} editing={editing}
            renderItem={(item) => (
              <span className="text-sm text-amber-700 dark:text-amber-300 flex items-start gap-2">
                <span className="text-amber-500 mt-0.5">&#9888;</span> {item}
              </span>
            )}
          />
        </div>
      )}

      {/* Breaking Changes */}
      {c.breaking_changes?.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/30 p-4">
          <SectionHeader icon={AlertTriangle} label="Breaking Changes" color="text-red-600 dark:text-red-400" />
          <ul className="space-y-1">
            {c.breaking_changes.map((item, i) => (
              <li key={i} className="text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
                <span className="text-red-500 mt-0.5">&#9888;</span> {item}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Upgrade Notes */}
      {(c.upgrade_notes || editing) && (
        <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-700/30 p-4">
          <SectionHeader icon={Code} label="Upgrade Notes" color="text-blue-600 dark:text-blue-400" />
          <EditableText value={c.upgrade_notes} onChange={(v) => setField('upgrade_notes', v)} editing={editing} multiline className="text-sm text-blue-700 dark:text-blue-300" />
        </div>
      )}
    </>
  );
}

// Fallback for risks_and_blockers shared across types
function renderRisksAndBlockers(c, editing, setField) {
  if (!c.risks_and_blockers?.length && !editing) return null;
  return (
    <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700/30 p-4">
      <SectionHeader icon={AlertTriangle} label="Risks & Blockers" color="text-amber-600 dark:text-amber-400" />
      <ul className="space-y-2">
        {(c.risks_and_blockers || []).map((item, i) => (
          <li key={i} className="text-sm text-amber-700 dark:text-amber-300">
            {typeof item === 'object' ? (
              <div>
                <span className="font-medium">{item.risk}</span>
                {item.impact && <Badge className={`ml-2 text-[10px] ${
                  item.impact === 'High' ? 'bg-red-100 text-red-700' :
                  item.impact === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                }`}>{item.impact}</Badge>}
                {item.mitigation && <p className="text-xs mt-1 text-amber-600 dark:text-amber-400">&rarr; {item.mitigation}</p>}
              </div>
            ) : (
              <span className="flex items-start gap-2"><span className="text-amber-500 mt-0.5">&#9888;</span> {item}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// Route to the correct renderer
function renderReportContent(reportType, content, editing, setField) {
  const renderers = {
    weekly: renderWeeklyReport,
    sprint_wrap: renderSprintWrap,
    sprint_summary: renderSprintSummary,
    stakeholder_update: renderStakeholderUpdate,
    team_sprint_report: renderTeamSprintReport,
    executive: renderExecutiveReport,
    release_notes: renderReleaseNotes,
  };

  const renderer = renderers[reportType];
  if (!renderer) {
    // Generic fallback
    return renderWeeklyReport(content, editing, setField);
  }

  return (
    <>
      {renderer(content, editing, setField)}
      {/* Shared risks section for types that use it */}
      {!['executive', 'team_sprint_report'].includes(reportType) && renderRisksAndBlockers(content, editing, setField)}
    </>
  );
}

// =============================================
// MAIN COMPONENT
// =============================================

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [standups, setStandups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  // Generation form
  const [reportType, setReportType] = useState('weekly');
  const [audience, setAudience] = useState('executive');
  const [sendEmail, setSendEmail] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');

  // Sprints and products for selection
  const [sprints, setSprints] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedSprint, setSelectedSprint] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');

  // View modal
  const [selectedReport, setSelectedReport] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);

  // Edit mode
  const [editing, setEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(null);
  const [saving, setSaving] = useState(false);

  // Email modal
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailAddress, setEmailAddress] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  // Tab — URL-synced so deep links and the old /reports/templates redirect land
  // on the right tab. Unknown values silently fall back to the default.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const activeTab = REPORTS_TAB_KEYS.includes(rawTab) ? rawTab : REPORTS_DEFAULT_TAB;
  const setActiveTab = (next) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next === REPORTS_DEFAULT_TAB) params.delete('tab'); else params.set('tab', next);
      return params;
    }, { replace: true });
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [reportsRes, standupsRes, sprintsRes, productsRes] = await Promise.all([
        api.get('/reports/'),
        api.get('/reports/standups/'),
        api.get('/sprints/'),
        api.get('/products/')
      ]);
      setReports(reportsRes.data);
      setStandups(standupsRes.data);
      setSprints(sprintsRes.data);
      setProducts(productsRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async () => {
    setGenerating(true);
    try {
      const payload = {
        report_type: reportType,
        audience: audience,
        sprint_id: selectedSprint || null,
        product_id: selectedProduct || null,
        send_email: sendEmail,
        recipient_email: sendEmail ? recipientEmail : null
      };

      await api.post('/reports/generate', payload);
      await loadData();

      setSendEmail(false);
      setRecipientEmail('');
    } catch (err) {
      console.error('Failed to generate report:', err);
      alert('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const viewReport = (report) => {
    setSelectedReport(report);
    setEditedContent(JSON.parse(JSON.stringify(report.content || {})));
    setEditing(false);
    setShowReportModal(true);
  };

  const saveEdits = async () => {
    if (!selectedReport || !editedContent) return;
    setSaving(true);
    try {
      const res = await api.patch(`/reports/${selectedReport.id}`, { content: editedContent });
      // Update local state
      setSelectedReport({ ...selectedReport, content: editedContent });
      setReports(reports.map(r => r.id === selectedReport.id ? { ...r, content: editedContent } : r));
      setEditing(false);
    } catch (err) {
      console.error('Failed to save report:', err);
      alert('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const cancelEdits = () => {
    setEditedContent(JSON.parse(JSON.stringify(selectedReport?.content || {})));
    setEditing(false);
  };

  const setField = (key, value) => {
    setEditedContent(prev => ({ ...prev, [key]: value }));
  };

  const deleteReport = async (reportId) => {
    if (!confirm('Delete this report?')) return;
    try {
      await api.delete(`/reports/${reportId}`);
      await loadData();
    } catch (err) {
      console.error('Failed to delete report:', err);
    }
  };

  const openEmailModal = (item, type) => {
    setEmailTarget({ ...item, type });
    setEmailAddress('');
    setShowEmailModal(true);
  };

  const sendEmailToRecipient = async () => {
    if (!emailTarget || !emailAddress) return;
    setSendingEmail(true);
    try {
      const endpoint = emailTarget.type === 'standup'
        ? `/reports/standups/${emailTarget.id}/send-email`
        : `/reports/${emailTarget.id}/send-email`;

      await api.post(endpoint, {
        report_id: emailTarget.id,
        recipient_email: emailAddress
      });

      setShowEmailModal(false);
      await loadData();
      alert('Email sent successfully!');
    } catch (err) {
      console.error('Failed to send email:', err);
      alert('Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-[#98139C]/30 border-t-[#98139C] rounded-full animate-spin" />
      </div>
    );
  }

  const displayContent = editing ? editedContent : (selectedReport?.content || {});

  return (
    <div className="p-4 lg:p-8" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6 lg:mb-10">
        <div>
          <p className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#98139C]">Communication</p>
          <h1 className="text-2xl lg:text-4xl font-heading text-[#30302F] dark:text-white mt-1" style={{ letterSpacing: '-0.8px' }}>
            Reports
          </h1>
          <p className="text-[#30302F] dark:text-white mt-1 lg:mt-2 font-light text-sm lg:text-lg">
            Generate stakeholder reports and daily standups — and edit the AI templates that produce them.
          </p>
        </div>
      </div>

      {/* Generate Report Card */}
      <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-6 mb-6">
        <h3 className="font-bold text-[#30302F] dark:text-white mb-4 flex items-center gap-2">
          <Sparkles size={18} className="text-[#98139C]" /> Generate New Report
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* Report Type */}
          <div>
            <label className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-2 block">
              Report Type
            </label>
            <CustomSelect
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              data-testid="report-type-select"
              className="w-full"
              options={[
                { value: 'sprint_wrap', label: 'Sprint Wrap (team email)' },
                { value: 'weekly', label: 'Weekly Progress' },
                { value: 'sprint_summary', label: 'Sprint Summary' },
                { value: 'stakeholder_update', label: 'Stakeholder Update' },
                { value: 'team_sprint_report', label: 'Team Sprint Report' },
                { value: 'executive', label: 'Executive Update' },
                { value: 'release_notes', label: 'Release Notes' },
              ]}
            />
          </div>

          {/* Audience */}
          <div>
            <label className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-2 block">
              Audience
            </label>
            <CustomSelect
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              data-testid="audience-select"
              className="w-full"
              options={[
                { value: 'team', label: 'Team (internal wrap)' },
                { value: 'executive', label: 'Executive' },
                { value: 'technical', label: 'Technical' },
                { value: 'customer', label: 'Customer' },
                { value: 'stakeholder', label: 'Stakeholder' },
              ]}
            />
          </div>

          {/* Sprint (optional) */}
          <div>
            <label className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-2 block">
              Sprint (Optional)
            </label>
            <CustomSelect
              value={selectedSprint}
              onChange={(e) => setSelectedSprint(e.target.value)}
              className="w-full"
              options={[
                { value: '', label: 'All Sprints' },
                ...sprints.map(s => ({ value: s.id, label: s.name }))
              ]}
            />
          </div>

          {/* Product (optional) */}
          <div>
            <label className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-2 block">
              Product (Optional)
            </label>
            <CustomSelect
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value)}
              className="w-full"
              options={[
                { value: '', label: 'All Products' },
                ...products.map(p => ({ value: p.id, label: p.name }))
              ]}
            />
          </div>
        </div>

        {/* Email option */}
        <div className="flex items-center gap-4 mb-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              className="w-4 h-4 accent-[#98139C]"
            />
            <span className="text-sm text-[#30302F] dark:text-white">Send via email</span>
          </label>
          {sendEmail && (
            <Input
              type="email"
              placeholder="recipient@example.com"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              className="w-64 h-9 rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-white/5 dark:text-white focus-visible:ring-[#98139C]"
            />
          )}
        </div>

        <button
          onClick={generateReport}
          disabled={generating || (sendEmail && !recipientEmail)}
          className="bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold text-sm px-6 h-10 flex items-center gap-2 transition-colors disabled:opacity-60"
          data-testid="generate-report-btn"
        >
          {generating ? (
            <><Loader2 size={16} className="animate-spin" /> Generating...</>
          ) : (
            <><Sparkles size={16} /> Generate Report</>
          )}
        </button>
      </div>

      {/* Tabs — Reports | Standups | Templates. Templates is the AI template editor;
          live preview via /reports/test-generate is one click away. */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 h-10 p-1">
          <TabsTrigger value="reports" className={REPORTS_TAB_TRIGGER_CLASS} data-testid="reports-tab">
            <FileText size={14} className="mr-1.5" /> Reports ({reports.length})
          </TabsTrigger>
          <TabsTrigger value="standups" className={REPORTS_TAB_TRIGGER_CLASS} data-testid="standups-tab">
            <Activity size={14} className="mr-1.5" /> Standups ({standups.length})
          </TabsTrigger>
          <TabsTrigger value="templates" className={REPORTS_TAB_TRIGGER_CLASS} data-testid="templates-tab">
            <SettingsIcon size={14} className="mr-1.5" /> Templates
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Templates tab is its own self-contained editor; renders inline. */}
      {activeTab === 'templates' && (
        <div className="mt-2">
          <ReportTemplatesPanel />
        </div>
      )}

      {/* Reports List */}
      {activeTab === 'reports' && (
        <div className="space-y-3">
          {reports.length === 0 ? (
            <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-8 text-center">
              <FileText size={32} className="text-[#30302F]/30 dark:text-white/30 mx-auto mb-3" />
              <p className="text-[#30302F] dark:text-white">No reports generated yet</p>
              <p className="text-sm text-[#30302F]/80 dark:text-white/80 mt-1">Generate your first report above</p>
            </div>
          ) : (
            reports.map(report => {
              const typeConfig = reportTypeConfig[report.report_type] || reportTypeConfig.weekly;
              const TypeIcon = typeConfig.icon;

              return (
                <div
                  key={report.id}
                  className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4 hover:border-[#98139C] transition-colors"
                  data-testid={`report-card-${report.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-full ${typeConfig.color} flex items-center justify-center`}>
                        <TypeIcon size={18} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-bold text-[#30302F] dark:text-white">{typeConfig.label}</h4>
                          <Badge className="text-[10px] bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-white/80">
                            {audienceConfig[report.audience]?.label || report.audience}
                          </Badge>
                          {report.sent_email && (
                            <Badge className="text-[10px] bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400">
                              <Mail size={10} className="mr-1" /> Sent
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-[#30302F]/80 dark:text-white/80 mt-1">
                          <Clock size={10} className="inline mr-1" />
                          {formatDate(report.generated_at)}
                          {(report.content?.executive_summary || report.content?.summary || report.content?.headline) && (
                            <span className="ml-2">&mdash; {(report.content.executive_summary || report.content.summary || report.content.headline).substring(0, 80)}...</span>
                          )}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => viewReport(report)}
                        className="p-2 rounded-full bg-gray-100 dark:bg-white/10 text-[#30302F] dark:text-white hover:bg-gray-200 dark:hover:bg-white/20 transition-colors"
                        title="View Report"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={() => openEmailModal(report, 'report')}
                        className="p-2 rounded-full bg-[#f0dcf0] dark:bg-[#98139C]/20 text-[#98139C] hover:bg-[#e0cce0] transition-colors"
                        title="Send Email"
                      >
                        <Mail size={14} />
                      </button>
                      <button
                        onClick={() => deleteReport(report.id)}
                        className="p-2 rounded-full bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/30 transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Standups List */}
      {activeTab === 'standups' && (
        <div className="space-y-3">
          {standups.length === 0 ? (
            <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-8 text-center">
              <Activity size={32} className="text-[#30302F]/30 dark:text-white/30 mx-auto mb-3" />
              <p className="text-[#30302F] dark:text-white">No standups generated yet</p>
              <p className="text-sm text-[#30302F]/80 dark:text-white/80 mt-1">Generate standups from the Sprint Board</p>
            </div>
          ) : (
            standups.map(standup => {
              const healthStatus = standup.standup?.health_status || 'unknown';
              const healthColor = healthStatus === 'on_track' ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400' :
                                 healthStatus === 'at_risk' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400' :
                                 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400';

              return (
                <div
                  key={standup.id}
                  className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4 hover:border-[#0ea5e9] transition-colors"
                  data-testid={`standup-card-${standup.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#0ea5e9]/10 text-[#0ea5e9] flex items-center justify-center">
                        <Activity size={18} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-bold text-[#30302F] dark:text-white">{standup.sprint_name}</h4>
                          <Badge className={`text-[10px] ${healthColor}`}>
                            {healthStatus === 'on_track' ? 'On Track' : healthStatus === 'at_risk' ? 'At Risk' : 'Behind'}
                          </Badge>
                          {standup.sent_email && (
                            <Badge className="text-[10px] bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400">
                              <Mail size={10} className="mr-1" /> Sent
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-[#30302F]/80 dark:text-white/80 mt-1">
                          <Clock size={10} className="inline mr-1" />
                          {formatDate(standup.generated_at)}
                          {standup.standup?.summary && (
                            <span className="ml-2">&mdash; {standup.standup.summary.substring(0, 80)}...</span>
                          )}
                        </p>
                        {standup.standup?.burndown && (
                          <div className="flex items-center gap-3 mt-2 text-xs">
                            <span className="text-green-600 dark:text-green-400">&#10003; {standup.standup.burndown.completed_points} pts done</span>
                            <span className="text-[#98139C]">{standup.standup.burndown.remaining_points} remaining</span>
                            <span className="text-[#4F0077] dark:text-[#d87bdb] font-bold">{standup.standup.burndown.percent_complete}%</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => { setSelectedReport(standup); setEditedContent(null); setEditing(false); setShowReportModal(true); }}
                        className="p-2 rounded-full bg-gray-100 dark:bg-white/10 text-[#30302F] dark:text-white hover:bg-gray-200 dark:hover:bg-white/20 transition-colors"
                        title="View Standup"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={() => openEmailModal(standup, 'standup')}
                        className="p-2 rounded-full bg-[#0ea5e9]/10 text-[#0ea5e9] hover:bg-[#0ea5e9]/20 transition-colors"
                        title="Send Email"
                      >
                        <Mail size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* View Report Modal */}
      <Dialog open={showReportModal} onOpenChange={(open) => { setShowReportModal(open); if (!open) { setEditing(false); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-[#1a1a1a]">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-xl font-heading text-[#30302F] dark:text-white flex items-center gap-2">
                <FileText size={20} className="text-[#98139C]" />
                {selectedReport?.report_type ? reportTypeConfig[selectedReport.report_type]?.label || 'Report' : 'Standup'}
              </DialogTitle>
              {/* Edit / Save / Cancel buttons — only for reports (not standups) */}
              {selectedReport?.content && (
                <div className="flex items-center gap-2">
                  {editing ? (
                    <>
                      <button
                        onClick={saveEdits}
                        disabled={saving}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full text-xs font-bold transition-colors disabled:opacity-60"
                      >
                        {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
                      </button>
                      <button
                        onClick={cancelEdits}
                        className="flex items-center gap-1.5 px-3 py-1.5 border border-[#e3e3e3] dark:border-white/10 text-[#30302F] dark:text-white rounded-full text-xs font-bold hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                      >
                        <X size={12} /> Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setEditing(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 border border-[#e3e3e3] dark:border-white/10 text-[#30302F] dark:text-white rounded-full text-xs font-bold hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                    >
                      <Pencil size={12} /> Edit
                    </button>
                  )}
                </div>
              )}
            </div>
          </DialogHeader>

          {selectedReport && (
            <div className="space-y-4 mt-4">
              {/* Report content — type-specific renderer */}
              {selectedReport.content && renderReportContent(selectedReport.report_type, displayContent, editing, setField)}

              {/* Standup content */}
              {selectedReport.standup && (
                <>
                  <div className="bg-[#faf8fb] dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-4">
                    <p className="text-sm text-[#30302F] dark:text-white">{selectedReport.standup.summary}</p>
                  </div>

                  {selectedReport.standup.burndown && (
                    <div className="grid grid-cols-4 gap-3">
                      <MetricCard value={selectedReport.standup.burndown.completed_points} label="Done" color="text-[#10b981]" />
                      <MetricCard value={selectedReport.standup.burndown.remaining_points} label="Remaining" color="text-[#98139C]" />
                      <MetricCard value={selectedReport.standup.burndown.total_points} label="Total" />
                      <MetricCard value={`${selectedReport.standup.burndown.percent_complete}%`} label="Complete" color="text-[#4F0077]" />
                    </div>
                  )}

                  {selectedReport.standup.completed_yesterday?.length > 0 && (
                    <div>
                      <SectionHeader icon={Check} label="Completed" color="text-green-600 dark:text-green-400" />
                      <ul className="space-y-1">
                        {selectedReport.standup.completed_yesterday.map((item, i) => (
                          <li key={i} className="text-sm text-[#30302F] dark:text-white">{item.title}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedReport.standup.blocked?.length > 0 && (
                    <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/30 p-3">
                      <SectionHeader icon={AlertTriangle} label="Blocked" color="text-red-600 dark:text-red-400" />
                      <ul className="space-y-1">
                        {selectedReport.standup.blocked.map((item, i) => (
                          <li key={i} className="text-sm text-red-700 dark:text-red-300">{item.title}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}

              <div className="text-xs text-[#30302F]/80 dark:text-white/80 pt-4 border-t border-[#e3e3e3] dark:border-white/10">
                Generated: {formatDate(selectedReport.generated_at)}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="max-w-md rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-[#1a1a1a]">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading text-[#30302F] dark:text-white flex items-center gap-2">
              <Mail size={20} className="text-[#98139C]" />
              Send via Email
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <label className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-2 block">
                Recipient Email
              </label>
              <Input
                type="email"
                placeholder="recipient@example.com"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                className="rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-white/5 dark:text-white focus-visible:ring-[#98139C]"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={sendEmailToRecipient}
                disabled={sendingEmail || !emailAddress}
                className="flex-1 bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold h-10 flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
              >
                {sendingEmail ? (
                  <><Loader2 size={16} className="animate-spin" /> Sending...</>
                ) : (
                  <><Send size={16} /> Send Email</>
                )}
              </button>
              <button
                onClick={() => setShowEmailModal(false)}
                className="px-6 border-2 border-[#e3e3e3] dark:border-white/10 text-[#30302F] dark:text-white rounded-full font-bold h-10 hover:bg-[#f9f5fa] dark:hover:bg-white/5 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
